import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Course, UserSession, CourseProgress, resolveAssetUrl } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { api, progressStore } from '../services/api';
import { Play, CheckCircle, Shield, Clock, FileCheck, Gamepad2, BookOpen, Sparkles, Award, TrendingUp, CheckCircle2 } from 'lucide-react';

interface CatalogPageProps {
  session: UserSession;
}

export const CatalogPage: React.FC<CatalogPageProps> = ({ session }) => {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [badges, setBadges] = useState<string[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, CourseProgress>>({});
  const [overallStats, setOverallStats] = useState({ completedCourses: 0, totalCourses: 5, overallPercent: 0 });

  useEffect(() => {
    api.getCourses().then((courseList) => {
      setCourses(courseList);
      const userBadges = progressStore.getBadges(session.userId);
      setBadges(userBadges.map((b) => b.courseId));

      const pMap: Record<string, CourseProgress> = {};
      for (const c of courseList) {
        pMap[c.courseId] = progressStore.getCourseProgress(session.userId, c.courseId);
      }
      setProgressMap(pMap);
      setOverallStats(progressStore.getOverallProgress(session.userId, courseList.length || 5));
    });
  }, [session.userId]);

  return (
    <div style={{ padding: '32px 24px', maxWidth: '1280px', margin: '0 auto' }}>
      {/* Overall Learner Progress Banner */}
      <div
        className="glass-card"
        style={{
          padding: '24px 28px',
          borderRadius: 'var(--radius-lg)',
          marginBottom: '36px',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.95) 100%)',
          border: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
        }}
      >
        <div style={{ flex: '1 1 340px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '4px 10px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.18)', color: '#60a5fa', fontSize: '11.5px', fontWeight: 700, marginBottom: '8px' }}>
            <TrendingUp size={14} />
            {t('Tu Avance de Cumplimiento Corporativo', 'Your Corporate Compliance Progress')}
          </div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 6px', color: 'var(--text-primary)' }}>
            {overallStats.completedCourses} {t('de', 'of')} {overallStats.totalCourses} {t('Cursos Certificados', 'Courses Certified')} ({overallStats.overallPercent}%)
          </h2>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
            {overallStats.overallPercent === 100
              ? t('¡Felicidades! Has completado el 100% de tu plan de capacitación anual.', 'Congratulations! You completed 100% of your annual compliance training.')
              : t('Continúa completando tus módulos para asegurar el 100% de cumplimiento legal en tu organización.', 'Keep progressing through your modules to ensure 100% legal compliance.')}
          </p>
        </div>

        <div style={{ flex: '1 1 280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 700 }}>
            <span style={{ color: 'var(--text-muted)' }}>{t('Progreso Global Acumulado', 'Total Progress Index')}</span>
            <span style={{ color: overallStats.overallPercent >= 80 ? '#34d399' : 'var(--accent-primary)' }}>
              {overallStats.overallPercent}%
            </span>
          </div>
          <div style={{ width: '100%', height: '10px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', overflow: 'hidden' }}>
            <div
              style={{
                width: `${overallStats.overallPercent}%`,
                height: '100%',
                background: overallStats.overallPercent === 100 ? 'linear-gradient(90deg, #10b981, #34d399)' : 'var(--gradient-brand)',
                transition: 'width 0.5s ease',
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-muted)' }}>
            <span>0% {t('Inicio', 'Start')}</span>
            <span>80% {t('Meta Mínima', 'Min Benchmark')}</span>
            <span>100% {t('Certificación Total', 'Total Certification')}</span>
          </div>
        </div>
      </div>

      {/* Hero Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '20px', background: 'rgba(59, 130, 246, 0.12)', border: '1px solid rgba(59, 130, 246, 0.3)', marginBottom: '10px', fontSize: '12px', color: '#60a5fa', fontWeight: 600 }}>
          <Sparkles size={14} />
          {t('Plan de Capacitación y Cumplimiento Corporativo 2026', 'Corporate Compliance & Training Plan 2026')}
        </div>
        <h1 style={{ fontSize: '30px', fontWeight: 800, marginTop: '4px', letterSpacing: '-0.5px' }}>
          {t('Catálogo de 5 Cursos Mandatorios', 'Mandatory 5-Course Compliance Suite')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14.5px', marginTop: '6px', maxWidth: '840px', lineHeight: '1.6' }}>
          {t(
            'Imágenes e infografías vectoriales bilingües de alta resolución. El progreso se guarda automáticamente en cada lección.',
            'High-definition bilingual vector diagrams and emblems. Course progress is automatically saved on every step.'
          )}
        </p>
      </div>

      {/* Course Cards Grid with Integrated Progress Bars */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: '28px' }}>
        {courses.map((course) => {
          const isCertified = badges.includes(course.courseId);
          const cp = progressMap[course.courseId];
          const coursePercent = cp?.progressPercent ?? (isCertified ? 100 : 0);
          const status = cp?.status ?? (isCertified ? 'COMPLETED' : 'NOT_STARTED');
          const totalPages = course.modules.reduce((acc, m) => acc + (m.pages?.length || 3), 0);

          const bannerSrc = resolveAssetUrl(course.bannerUrl, lang);
          const badgeSrc = resolveAssetUrl(course.badgeUrl, lang);

          return (
            <div
              key={course.courseId}
              className="glass-card"
              style={{
                padding: '0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                overflow: 'hidden',
                borderRadius: 'var(--radius-lg)',
                border: status === 'COMPLETED' ? '1.5px solid rgba(16, 185, 129, 0.4)' : status === 'IN_PROGRESS' ? '1.5px solid rgba(59, 130, 246, 0.4)' : '1px solid var(--border-color)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
            >
              {/* Card Banner Image Header */}
              <div
                style={{
                  height: '160px',
                  width: '100%',
                  position: 'relative',
                  backgroundImage: bannerSrc
                    ? `linear-gradient(to bottom, rgba(15, 23, 42, 0.1) 0%, rgba(15, 23, 42, 0.9) 100%), url(${bannerSrc})`
                    : 'var(--gradient-brand)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  padding: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                }}
                onClick={() => navigate(`/course/${course.courseId}`)}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <span
                    style={{
                      background: 'rgba(15, 23, 42, 0.85)',
                      backdropFilter: 'blur(8px)',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '12px',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                    }}
                  >
                    {course.category}
                  </span>
                  <span
                    style={{
                      background: 'rgba(59, 130, 246, 0.3)',
                      color: '#93c5fa',
                      fontSize: '10.5px',
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: '8px',
                      width: 'fit-content',
                    }}
                  >
                    {course.jurisdiction}
                  </span>
                </div>

                {badgeSrc && (
                  <img
                    src={badgeSrc}
                    alt="Badge"
                    style={{
                      width: '56px',
                      height: '56px',
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.6))',
                    }}
                  />
                )}
              </div>

              {/* Progress Bar Strip directly under Banner */}
              <div style={{ background: 'var(--bg-secondary)', padding: '8px 20px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {status === 'COMPLETED' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#34d399', fontSize: '11.5px', fontWeight: 700 }}>
                      <CheckCircle2 size={13} /> {t('Completado (100%)', 'Completed (100%)')}
                    </span>
                  ) : status === 'IN_PROGRESS' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#60a5fa', fontSize: '11.5px', fontWeight: 700 }}>
                      <Clock size={13} /> {t('En Progreso', 'In Progress')} ({coursePercent}%)
                    </span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '11.5px', fontWeight: 600 }}>
                      {t('No Iniciado (0%)', 'Not Started (0%)')}
                    </span>
                  )}
                </div>

                {/* Progress Bar Track */}
                <div style={{ width: '90px', height: '6px', background: 'var(--bg-primary)', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ width: `${coursePercent}%`, height: '100%', background: status === 'COMPLETED' ? '#10b981' : 'var(--accent-primary)', transition: 'width 0.3s ease' }} />
                </div>
              </div>

              {/* Card Body */}
              <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <h3
                    style={{ fontSize: '17px', fontWeight: 700, marginBottom: '8px', lineHeight: 1.3, color: 'var(--text-primary)', cursor: 'pointer' }}
                    onClick={() => navigate(`/course/${course.courseId}`)}
                  >
                    {course.title[lang]}
                  </h3>

                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px', lineHeight: 1.5 }}>
                    {course.description[lang]}
                  </p>

                  {/* Micro-Learning & Gamification Stats Pills */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '11.5px', color: '#94a3b8', background: 'var(--bg-secondary)', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border-color)' }}>
                      <Clock size={12} /> {course.modules.length} {t('módulos + checks', 'modules + checks')}
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#94a3b8', background: 'var(--bg-secondary)', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid var(--border-color)' }}>
                      <BookOpen size={12} /> {totalPages} {t('micro-lecciones ilustradas', 'illustrated micro-lessons')}
                    </div>
                    {course.miniGame && (
                      <div style={{ fontSize: '11.5px', color: '#34d399', background: 'rgba(16, 185, 129, 0.12)', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(16, 185, 129, 0.25)' }}>
                        <Gamepad2 size={12} /> {t('Mini-Juego (100 pts)', 'Mini-Game (100 pts)')}
                      </div>
                    )}
                    <div style={{ fontSize: '11.5px', color: '#60a5fa', background: 'rgba(59, 130, 246, 0.12)', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid rgba(59, 130, 246, 0.25)' }}>
                      <FileCheck size={12} /> {t('12 Preguntas Aleatorias', '12 Random Questions')}
                    </div>
                  </div>
                </div>

                {/* Card Footer */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '14px', borderTop: '1px solid var(--border-color)', marginBottom: '16px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {t('Aprobación:', 'Passing Score:')} <strong>{course.passingScore}%</strong>
                    </span>
                    <span style={{ fontSize: '12px', color: 'var(--accent-success)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Shield size={14} /> 365 {t('días vigencia', 'days vigency')}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ flex: 1, padding: '10px 12px', fontSize: '12.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      onClick={() => navigate(`/course/${course.courseId}`)}
                    >
                      <Play size={14} />
                      {status === 'COMPLETED' ? t('Repasar Curso', 'Review Course') : status === 'IN_PROGRESS' ? t('Continuar Curso', 'Resume Course') : t('Iniciar Curso', 'Start Course')}
                    </button>

                    <button
                      className={`btn ${status === 'COMPLETED' ? 'btn-success' : 'btn-primary'}`}
                      style={{ flex: 1, padding: '10px 12px', fontSize: '12.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                      onClick={() => navigate(`/course/${course.courseId}?tab=exam`)}
                    >
                      {status === 'COMPLETED' ? <Award size={14} /> : <FileCheck size={14} />}
                      {status === 'COMPLETED' ? t('Recertificar', 'Recertify') : t('Evaluación (12Q)', 'Take Exam (12Q)')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
